import { Box, TypographyProps } from "@mui/material";
import { FunctionComponent, useCallback, useMemo } from "react";
import { ArrayField, IArrayFieldComponentComponentProps, IArrayFieldHeaderFooterComponentProps, IArrayFieldNoRecordsComponentProps, IArrayFieldTemplateComponentProps } from '@ngt/forms-core';
import { faExclamationTriangle } from "@fortawesome/pro-light-svg-icons";
import { LoadingButton } from "@mui/lab";
import { BasicAlert } from "@ngt/forms";
import React from "react";

interface ITemplateProps extends IArrayFieldTemplateComponentProps {

}

const TemplateComponent: FunctionComponent<ITemplateProps> = ({
    ArrayFieldProps: {
        header,
        content,
        footer,
        arrayActions: {
            move
        },
        actions: {
            setValue
        }
    }
}) => {

    return (
        <>
            {header}
            {content}
            {footer}
        </>
    );
};


interface INoRecordsProps extends IArrayFieldNoRecordsComponentProps {
    title: string;
}

const NoRecordsComponent: FunctionComponent<INoRecordsProps> = () => {
    return (
        <BasicAlert
        />
    );
}

interface IHeaderProps extends IArrayFieldHeaderFooterComponentProps {
    title: string;
    titleVariant: TypographyProps['variant']
}

const HeaderComponent: FunctionComponent<IHeaderProps> = ({
    ArrayFieldProps: {
        actions: {
            setValue
        },
        count
    }
}) => {
    const onAdd = useCallback(async () => {
        setValue(currentItems => {
            return [...(currentItems ?? []), undefined];
        });
    }, [setValue]);
    if(count >= 2){
        return null;
    }
    return (
        <Box>
            <LoadingButton variant="outlined" onClick = {onAdd} >
                Add Speciality
            </LoadingButton>
        </Box>
    );
}

interface IComponentProps<TItemComponent extends ItemComponentType = any> extends IArrayFieldComponentComponentProps {
    itemComponent: TItemComponent;
    ItemComponentProps?: Omit<React.ComponentProps<TItemComponent>, 'index' | 'onDelete'>
}

type ItemComponentType<T extends IItemComponentProps = any> = React.ComponentType<T>;


interface IItemComponentProps {
    index: number;
    onDelete: (index: number) => void;
}

const ContentComponent: React.FunctionComponent<IComponentProps> = ({
    ArrayFieldProps: {
        index, 
        actions: {
            setValue
        },
        count
    },
    ItemComponentProps,
    itemComponent
}) => {
    const ItemComponent = itemComponent;

    const onDelete = useCallback((indexToDelete: number) => {
        setValue(currentItems => {
            return currentItems?.filter((x, i) => i !== indexToDelete);
        });
    }, [setValue]);

    if (index < 2) {
        return (
                <div>
                    {
                        !ItemComponent && (
                            <BasicAlert
                                title="No Item Component"
                                description={(
                                    <>
                                        No Item Component has been provided.
                                    </>
                                )}
                                severity="warning"
                                icon={faExclamationTriangle}
                            />
                        )
                    }
                    {
                        !!ItemComponent && (
                            <ItemComponent
                                {...ItemComponentProps}
                                index={index}
                                onDelete={onDelete}
                            />
                        )
                    }
                    {(index + 1) < count}
                    
                </div>
        )
    }
    else
        return(<p></p>)
}

interface ISpecialityArrayField<TItemComponent extends ItemComponentType = any> {
    propertyPath: string;
    getItemKey?: (index: number, item?: any | null) => string;
    itemComponent: TItemComponent;
    ItemComponentProps?: Omit<React.ComponentProps<TItemComponent>, 'index' | 'onDelete'>;
    title: string;
    titleVariant: TypographyProps['variant'];
    noRecordTitle: string;
}

const DefaultGetItemKey = (index: number) => `index-${index}`;

const SpecialityArrayField = <TItemComponent extends ItemComponentType = any>({
    getItemKey,
    itemComponent,
    ItemComponentProps,
    title,
    titleVariant,
    propertyPath,
    noRecordTitle
}: ISpecialityArrayField<TItemComponent>) => {

    const componentProps = useMemo(() => {
        return {
            itemComponent: itemComponent,
            ItemComponentProps: ItemComponentProps
        };

    }, [itemComponent, ItemComponentProps]);

    return (
        <>
            <ArrayField 
                propertyPath={propertyPath}
                getItemKey={getItemKey ?? DefaultGetItemKey}
                template={TemplateComponent}
                header={HeaderComponent}
                HeaderProps={{
                    title: title,
                    titleVariant: titleVariant
                }}
                FooterProps={{
                }}
                component={ContentComponent}
                ComponentProps={componentProps}
                noRecords={NoRecordsComponent}
                NoRecordsProps={{
                    title: noRecordTitle
                }}
            />
        </>
    );
};

export default SpecialityArrayField;